import {combine, createEvent, createStore} from 'effector';

import {
    clearDocumentsGridCache,
    fetchBlockingTableDictionary,
    fetchProductBlockingTable,
    removeCodeLock,
    toProductBlockingDictionaryRequest,
} from 'api';
import {createGridTableModel} from 'core/hooks/grid2.0';
import {rootDomain} from 'core/rootDomain';
import {CodeBlockingData} from 'types';
import {SimplePaginationContent} from 'types/api';
import {
    ProductBlockingCodesResponseData,
    ProductBlockingTableDictionary,
    RemoveCodeLockPayload,
} from 'types/products-blocking';

export const domain = rootDomain.createDomain('CodeLocksDomain');
export const tableDomain = rootDomain.createDomain('DocumentsGridTableDomain');

export const fetchDataFx = domain.createEffect({
    name: 'fetchDocumentListFx',
    handler: fetchProductBlockingTable,
});

export const fetchDictionaryFx = domain.createEffect({
    name: 'fetchBlockingTableDictionaryFx',
    handler: (params: Parameters<typeof fetchProductBlockingTable>[0]) =>
        fetchBlockingTableDictionary(toProductBlockingDictionaryRequest(params)),
});

export const clearDataCacheFx = domain.createEffect({
    name: 'clearDataCacheFx',
    handler: clearDocumentsGridCache,
});

export const RemoveLockCodeFx = domain.createEffect({
    name: 'RemoveLockCodeFx',
    handler: removeCodeLock,
});

export const setRemoveLockModalOpened = createEvent<CodeBlockingData | null>();
export const $removeLockModalOpened = createStore<CodeBlockingData | null>(null);

export const removeLockCode = createEvent<RemoveCodeLockPayload>();

export const $isRemovingLock = RemoveLockCodeFx.pending;

export const {
    filters$,
    filtersChanged,
    filtersReset,
    defaultFilters,
    sortings$,
    sortingsChanged,
    sortingsReset,
    defaultSortings,
    pageNumber$,
    pageNumberChanged,
    pageSize$,
    pageSizeChanged,
    Gate,
    tableStateChanged,
    tableStateCacheChanged,
    stateReset,
} = createGridTableModel({
    domain,
    defaultSortings: {createdAt: true},
});

export const data$ = domain.createStore<SimplePaginationContent<
    ProductBlockingCodesResponseData
> | null>(null);
export const dictionary$ = domain.createStore<ProductBlockingTableDictionary>({});

export const searchChanged = domain.createEvent<string>();
export const searchReset = domain.createEvent();
export const search$ = domain
    .createStore<string>('')
    .on(searchChanged, (_, value) => value)
    .reset(searchReset);

export const refetchWithSortByDescDate = domain.createEvent();

export const gridCacheChanged = domain.createEvent<{pageNumber: number; token: string | null}>();
export const gridCacheReset = domain.createEvent();
export const gridTokensCache$ = domain.createStore<Record<number, string | null>>({0: null});

export const tableState$ = combine({
    data: data$,
    dictionary: dictionary$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    filters: filters$,
    sortings: sortings$,
    search: search$,
    gridTokensCache: gridTokensCache$,
    isLoading: fetchDataFx.pending || fetchDictionaryFx.pending,
});
